import { useMemo } from 'react';
import type { UISelectionType } from '@components/types';
import { ResourceState } from '@domain/schemas/contract.schema';
import type { SubmissionShort } from '@queries/fetchSubmissions';
import { sortByDate } from '@root/helpers';

export interface DuplicateEndorsementOption extends UISelectionType {
  isCurrentContract: boolean;
}

interface UseDuplicateEndorsementContractListProps {
  contracts: SubmissionShort[];
  currentContractId?: string;
}

function hasDuplicableEndorsement(contract: SubmissionShort) {
  return contract.status === ResourceState.FINAL && (contract.endorsementsCount ?? 0) > 0;
}

export function useDuplicateEndorsementContractList({
  contracts,
  currentContractId,
}: UseDuplicateEndorsementContractListProps): DuplicateEndorsementOption[] {
  return useMemo(() => {
    const eligibleContracts = sortByDate(contracts.filter(hasDuplicableEndorsement), 'desc', 'created_at');

    return eligibleContracts
      .sort((a, b) => {
        const isCurrentA = a.id === currentContractId;
        const isCurrentB = b.id === currentContractId;
        return isCurrentA === isCurrentB ? 0 : isCurrentA ? -1 : 1;
      })
      .map((contract) => ({
        name: contract.name,
        value: contract.id,
        isCurrentContract: contract.id === currentContractId,
      }));
  }, [contracts, currentContractId]);
}
